import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTheme } from "next-themes";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AppLayout } from "@/components/layout";
import { Moon, Sun, Calendar, LayoutGrid } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  isNotificationsEnabled,
  getNotificationPermission,
} from "@/lib/notifications";
import { useUserPreferences } from "@/hooks/useUserPreferences";

const profileSchema = z.object({
  full_name: z
    .string()
    .min(2, "Full name must be at least 2 characters")
    .optional()
    .or(z.literal("")),
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores")
    .optional()
    .or(z.literal("")),
  avatar_url: z.string().url("Must be a valid URL").optional().or(z.literal("")),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

export function Settings() {
  const { theme, setTheme } = useTheme();
  const { user, profile, loading: authLoading, refreshProfile } = useAuth();
  const { homepageView, setHomepageView } = useUserPreferences();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notificationsEnabled] = useState(isNotificationsEnabled());
  const [notificationPermission] = useState(getNotificationPermission());

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: "",
      username: "",
      avatar_url: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    // Reset form when profile data changes
    if (profile) {
      form.reset({
        full_name: profile.full_name || "",
        username: profile.username || "",
        avatar_url: profile.avatar_url || "",
      });
    }

    }, [form, profile]);

  async function onSubmit(values: ProfileFormValues) {
    if (!user) return;

    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      // Check if username is already taken (if changed)
      if (values.username && values.username !== profile?.username) {
        const { data: existingProfile } = await supabase
          .from("profiles")
          .select("id")
          .eq("username", values.username)
          .single();

        if (existingProfile && existingProfile.id !== user.id) {
          setError("Username is already taken");
          setSaving(false);
          return;
        }
      }

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          full_name: values.full_name || null,
          username: values.username || null,
          avatar_url: values.avatar_url || null,
        })
        .eq("id", user.id);

      if (updateError) {
        throw updateError;
      }

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: values.full_name ? values.full_name : null,
          username: values.username ? values.username : null,
          avatar_url: values.avatar_url ? values.avatar_url : null,
        },
      });

      if (authError) {
        console.error("Error updating auth metadata:", authError);
        // Don't throw here, profile update was successful
      }

      // Refresh profile via context
      await refreshProfile();
      setSuccess("Profile updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update profile";
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (authLoading) {
    return (
      <AppLayout title="Settings">
        <div className="w-full">
          <Card>
            <CardContent className="py-8">
              <div className="text-sm text-muted-foreground text-center">Loading profile…</div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (error && !profile) {
    return (
      <AppLayout title="Settings">
        <div className="w-full">
          <Card>
            <CardContent className="py-8">
              <div className="text-sm text-destructive text-center">{error}</div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Settings">
        <div className="w-full space-y-6">
          {/* Profile Section */}
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Update your profile information</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Avatar Preview */}
                  <div className="flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage
                        src={form.watch("avatar_url") || profile?.avatar_url || undefined}
                        alt={profile?.full_name || "User"}
                      />
                      <AvatarFallback className="text-2xl">
                        {getInitials(form.watch("full_name") || profile?.full_name || null)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">
                        {form.watch("full_name") || profile?.full_name || "User"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        @{form.watch("username") || profile?.username || "username"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="full_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full name</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Your full name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Username</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="username" />
                          </FormControl>
                          <FormMessage />
                          <p className="text-xs text-muted-foreground">
                            Username can only contain letters, numbers, and underscores
                          </p>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="avatar_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Avatar URL</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="https://example.com/avatar.jpg"
                              type="url"
                            />
                          </FormControl>
                          <FormMessage />
                          <p className="text-xs text-muted-foreground">
                            Enter a URL to your profile picture
                          </p>
                        </FormItem>
                      )}
                    />
                  </div>

                  {error && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md border border-green-500/30 bg-green-500/5 p-3 text-sm text-green-600 dark:text-green-400">
                      {success}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button type="submit" disabled={saving}>
                      {saving ? "Saving…" : "Save changes"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        form.reset({
                          full_name: profile?.full_name || "",
                          username: profile?.username || "",
                          avatar_url: profile?.avatar_url || "",
                        });
                        setError(null);
                        setSuccess(null);
                      }}
                      disabled={saving}
                    >
                      Reset
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Theme Section */}
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Choose your preferred theme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="theme-toggle" className="text-sm font-medium">
                    Dark mode
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark theme
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={theme === "light" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTheme("light")}
                    className="h-8"
                  >
                    <Sun className="h-4 w-4 mr-1" />
                    Light
                  </Button>
                  <Button
                    variant={theme === "dark" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTheme("dark")}
                    className="h-8"
                  >
                    <Moon className="h-4 w-4 mr-1" />
                    Dark
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Homepage Section */}
          <Card>
            <CardHeader>
              <CardTitle>Homepage</CardTitle>
              <CardDescription>Choose your default homepage view</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Default view</Label>
                  <p className="text-sm text-muted-foreground">
                    This will be your first page after logging in
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={homepageView === "calendar" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHomepageView("calendar")}
                    className="h-8"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Calendar
                  </Button>
                  <Button
                    variant={homepageView === "kanban" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHomepageView("kanban")}
                    className="h-8"
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    Kanban
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>Daily task summary at 4pm</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">
                  {notificationsEnabled && notificationPermission === "granted"
                    ? "Enabled"
                    : notificationPermission === "denied"
                      ? "Permission denied"
                      : "Not enabled"}
                </p>
                {notificationPermission === "denied" && (
                  <p className="text-xs text-muted-foreground">
                    Enable notifications in your browser settings to receive daily summaries.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
  );
}